export interface CanadianProvince {
  name: string
  code: string
}

export const CANADIAN_PROVINCES = [
  { name: 'Alberta', code: 'AB' },
  { name: 'British Columbia', code: 'BC' },
  { name: 'Manitoba', code: 'MB' },
  { name: 'New Brunswick', code: 'NB' },
  { name: 'Newfoundland and Labrador', code: 'NL' },
  { name: 'Nova Scotia', code: 'NS' },
  { name: 'Northwest Territories', code: 'NT' },
  { name: 'Nunavut', code: 'NU' },
  { name: 'Ontario', code: 'ON' },
  { name: 'Prince Edward Island', code: 'PE' },
  { name: 'Quebec', code: 'QC' },
  { name: 'Saskatchewan', code: 'SK' },
  { name: 'Yukon', code: 'YT' },
] as const satisfies readonly CanadianProvince[]

export type CanadianProvinceCode = (typeof CANADIAN_PROVINCES)[number]['code']

export type NonOperatingCanadianProvinceCode =
  (typeof NON_OPERATING_CANADIAN_PROVINCE_CODES)[number]

/* An ISO 3166-2 subdivision code for a Canadian province Owner operates in. */
export type OperatingCanadianProvinceCode = Exclude<
  CanadianProvinceCode,
  NonOperatingCanadianProvinceCode
>

export const CANADIAN_PROVINCE_CODES: readonly CanadianProvinceCode[] =
  CANADIAN_PROVINCES.map(({ code }) => code)

const CANADIAN_PROVINCES_BY_CODE = Object.freeze(
  Object.fromEntries(CANADIAN_PROVINCES.map((x) => [x.code, x]))
) as Record<CanadianProvinceCode, CanadianProvince>

const NON_OPERATING_CANADIAN_PROVINCE_CODES = [
  'QC',
] as const satisfies readonly CanadianProvinceCode[]

export const OPERATING_CANADIAN_PROVINCE_CODES: readonly OperatingCanadianProvinceCode[] =
  CANADIAN_PROVINCE_CODES.filter(
    (code): code is OperatingCanadianProvinceCode =>
      !NON_OPERATING_CANADIAN_PROVINCE_CODES.some(
        (excluded) => excluded === code
      )
  )

/** Looks up a {@link CanadianProvince} by its {@link CanadianProvinceCode}. */
export const lookupCanadianProvince = (
  provinceCode: CanadianProvinceCode
): CanadianProvince => CANADIAN_PROVINCES_BY_CODE[provinceCode]

/**
 * Narrows an arbitrary string to a Canadian province code, whether or not
 * Owner operates there.
 *
 * @param value province code to check
 */
export const isCanadianProvinceCode = (
  value: string | null | undefined
): value is CanadianProvinceCode =>
  CANADIAN_PROVINCE_CODES.some((code) => code === value)

/**
 * Narrows an arbitrary string to a Canadian province Owner operates in.
 * @param value province code to check
 */
export const isOperatingCanadianProvinceCode = (
  value: string | null | undefined
): value is OperatingCanadianProvinceCode =>
  OPERATING_CANADIAN_PROVINCE_CODES.some((code) => code === value)
